import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import {
  accountRolesQuery,
  addAccountRole,
  removeAccountRole,
  type AccountIam,
  type RoleAuditEntry,
} from '../../api/settingsUsers'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { failed, whenText } from './shared'

/**
 * Rolurile SUPLIMENTARE ale contului, in fila "Roles & access" (cerinta CEC
 * L18). Jurnalul schimbarilor e cardul de la capat (`AccountRoleChanges`).
 *
 * Rolul principal se alege din cardul de deasupra (campul "Primary role")
 * si e singurul pe care il vad portalul CEC si ph4.api. Cele suplimentare
 * exista numai in v4: drepturile omului aici sunt uniunea tuturor.
 *
 * Ce se poate da hotaraste SERVERUL (`grantable`): un rol cu drepturi pe care
 * cel care il da nu le are nu se poate alege. Butonul ascuns e o curtoazie;
 * poarta adevarata e `UserRoles::mayGrant`.
 */
export function AccountRoles({ userId }: { userId: number }) {
  const client = useQueryClient()
  const page = useQuery(accountRolesQuery(userId))
  const [chosen, setChosen] = useState('')
  const [busy, setBusy] = useState(false)

  if (page.error) return <div className="alert alert-danger">{failed(page.error)}</div>
  if (!page.data) return <Spinner />

  const data = page.data
  const grantable = data.available.filter((role) => role.grantable)
  const withheld = data.available.filter((role) => !role.grantable)

  const reload = () => {
    void client.invalidateQueries({ queryKey: ['settings', 'users', 'accounts', userId, 'roles'] })
  }

  const run = (action: Promise<unknown>, done: string) => {
    setBusy(true)
    void action
      .then(() => {
        toast(done, 'Success')
        setChosen('')
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <RaSection title="Additional roles" className="account-roles">
      <p className="od-ra-lead">
        On top of the primary role (<span data-testid="primary-role">{data.primary ? data.primary.name : 'None'}</span>
        ), in this portal only. Rights add up; the widest wins.
      </p>

      {data.iam ? <IamNote iam={data.iam} /> : null}

      {data.extra.length === 0 ? (
        <p className="od-ra-empty" data-testid="no-extra-roles">
          No additional roles.
        </p>
      ) : (
        <ul className="od-ra-chips" data-testid="extra-roles">
          {data.extra.map((role) => {
            const removable = data.mayAssign && !role.fromDirectory
            return (
              <li key={role.id} className={removable ? 'od-ra-chip od-ra-chip-removable' : 'od-ra-chip'}>
                <span>{role.name}</span>
                {role.fromDirectory ? (
                  <span className="od-ra-chip-note" data-testid="from-directory">
                    from a directory group
                  </span>
                ) : null}
                {removable ? (
                  <button
                    type="button"
                    className="od-ra-chip-x"
                    disabled={busy}
                    aria-label={`Remove role ${role.name}`}
                    title="Remove"
                    onClick={() => run(removeAccountRole(userId, role.id), 'Role removed')}
                  >
                    &times;
                  </button>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}

      {data.mayAssign ? (
        <div className="od-ra-add">
          <select
            className="form-control"
            aria-label="Add a role"
            value={chosen}
            onChange={(event) => setChosen(event.target.value)}
          >
            <option value="">Add a role...</option>
            {grantable.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
            {withheld.map((role) => (
              <option key={role.id} value={role.id} disabled>
                {role.name} (has rights you do not have)
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || chosen === ''}
            onClick={() => run(addAccountRole(userId, Number(chosen)), 'Role added')}
          >
            Add role
          </button>
        </div>
      ) : null}
    </RaSection>
  )
}

/**
 * Jurnalul schimbarilor de rol, in cardul lui de la capatul filei. Citeste
 * aceeasi cerere ca `AccountRoles` (react-query o face o singura data).
 */
export function AccountRoleChanges({ userId }: { userId: number }) {
  const page = useQuery(accountRolesQuery(userId))
  if (!page.data) return null
  return (
    <RaSection title="Role changes">
      <RoleAudit entries={page.data.audit} />
    </RaSection>
  )
}

/**
 * Un card al filei "Roles & access": `panel panel-default` (desenul zonei de
 * setari), cu titlul in cap. Foaia e `RolesAccess.css`.
 */
export function RaSection({
  title,
  className,
  children,
}: {
  title: string
  className?: string
  children: ReactNode
}) {
  return (
    <section className={`panel panel-default od-ra-section${className ? ` ${className}` : ''}`}>
      <div className="panel-heading">
        <h4 className="od-ra-title">{title}</h4>
      </div>
      <div className="panel-body">{children}</div>
    </section>
  )
}

/**
 * Contul e gestionat din grupurile IAM: ce vine de acolo (firme, roluri
 * suplimentare, drepturi) nu se schimba de aici - s-ar intoarce la urmatoarea
 * intrare sau sincronizare. Se schimba in director.
 */
export function IamNote({ iam }: { iam: AccountIam }) {
  const parts: string[] = []
  if (iam.companies) parts.push('companies')
  if (iam.extraRoles) parts.push('additional roles')
  if (!iam.keepManual) parts.push('permissions')
  return (
    <div className="alert alert-info small" data-testid="iam-note">
      <strong>Managed by {iam.provider}.</strong> The {parts.join(', ')} of this account come from its directory groups
      and cannot be changed here &mdash; change the groups in the directory.
      {iam.companies ? (
        <div className="mt-1" data-testid="iam-companies">
          Companies: {iam.companyNames.length === 0 ? 'none' : iam.companyNames.join(', ')}
        </div>
      ) : null}
    </div>
  )
}

const ACTIONS: Record<string, string> = {
  add: 'Added additional role',
  remove: 'Removed additional role',
  primary: 'Changed primary role',
}

export function roleAuditText(entry: RoleAuditEntry): string {
  const what = ACTIONS[entry.action] ?? entry.action
  if (entry.action === 'primary') {
    return `${what}: ${entry.previous || 'none'} -> ${entry.role}`
  }
  return `${what}: ${entry.role}`
}

function RoleAudit({ entries }: { entries: RoleAuditEntry[] }) {
  if (entries.length === 0) return <p className="od-ra-empty">No role changes recorded yet.</p>
  return (
    <table className="table table-condensed table-striped od-ra-audit" data-testid="role-audit">
      <thead>
        <tr>
          <th>When</th>
          <th>Change</th>
          <th>By</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, index) => (
          <tr key={`${entry.at}-${index}`}>
            <td>{whenText(entry.at)}</td>
            <td>{roleAuditText(entry)}</td>
            <td>{entry.actor || 'Directory (IAM)'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
